import { useEffect, useMemo } from 'react';
import { useMatch } from 'react-router-dom';

import { useCurrentOrganizationId } from '@cast/infrastructure-organization';
import { usePersistentState } from '@cast/utils';

import {
  dboDatabaseRoutePattern,
  dboServerRoutePattern,
  emptyDboParams,
  getDboSidebarParamsStorageKey,
  resolveDboSidebarParams,
  type DboSidebarParams,
} from './dboSidebarParamsStorage';

export const useDboUrlParams = (): DboSidebarParams => {
  const dbServerId = useMatch(dboServerRoutePattern)?.params?.dbServerId;
  const databaseId = useMatch(dboDatabaseRoutePattern)?.params?.databaseId;

  return { dbServerId, databaseId };
};

export const useDboParams = (): DboSidebarParams => {
  const organizationId = useCurrentOrganizationId();
  const { dbServerId, databaseId } = useDboUrlParams();

  const storageKey = getDboSidebarParamsStorageKey(organizationId ?? '');
  const [stored, setStored] = usePersistentState<DboSidebarParams>(
    storageKey,
    emptyDboParams,
    'session'
  );

  const resolved = useMemo(
    () =>
      resolveDboSidebarParams(stored ?? emptyDboParams, {
        databaseId,
        dbServerId,
      }),
    [stored, databaseId, dbServerId]
  );

  useEffect(() => {
    if (
      resolved.dbServerId !== stored?.dbServerId ||
      resolved.databaseId !== stored?.databaseId
    ) {
      setStored(resolved);
    }
  }, [resolved, stored, setStored]);

  return resolved;
};
